/**
 * Shared display formatters for the admin.
 *
 * House rules (enforced across every admin screen):
 *   - numeric quantities render with EXACTLY two decimals, never more, never less (30 -> "30.00");
 *   - money carries the руб sign ("30.00 ₽"), per-litre prices "30.00 ₽/л";
 *   - volumes carry the litre unit ("30.00 л");
 *   - fuel is shown by its Russian name (АИ-95), never its code (AI_95).
 *
 * Everything routes through {@link formatDecimal2}. It always emits a dot decimal separator
 * regardless of the browser locale, matching the Android Locale.US formatter and the dotted wire
 * format. Do NOT swap in Intl.NumberFormat — a locale comma would diverge from the operator app
 * and the стоимость calc. A null / empty / non-numeric value renders as an em dash.
 *
 * Rounding is done on the DECIMAL DIGITS directly (round-half-up), not via Number.prototype.toFixed:
 * toFixed is IEEE754-float-precision-lossy at exactly-half boundaries — famously
 * `(1.005).toFixed(2) === '1.00'`, not `'1.01'`, because 1.005 can't be represented exactly in
 * binary floating point. That boundary is not hypothetical here: `fuelAmount / 1000` (ml → l,
 * CLAUDE.md gotcha #4) routinely produces exact 3-decimal values like 26.615.
 *
 * The only thing NOT covered by the two-decimals rule (it carries its own precision): geographic
 * coordinates (lat/lon). Free-text numeric INPUT fields (editable price/rate/liters/capacity) ARE
 * covered too — see {@link toEditableDecimal2} — the wire value just can't carry a unit suffix or
 * an em dash the way a read-only display can.
 */

export const EM_DASH = '—';

/** Exactly two decimals, dot separator, no thousands grouping. `—` for null / empty / non-numeric. */
export function formatDecimal2(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  if (Number.isNaN(Number(value))) return EM_DASH;
  const raw = typeof value === 'number' ? value.toString() : value;
  return roundHalfUpToTwoDecimals(raw);
}

/**
 * Round-half-up to 2 decimal places, working on the digit string itself so binary floating-point
 * representation never enters the picture (see the module doc comment for why `toFixed` can't be
 * trusted here). Only ever called with a value that already passed `Number.isNaN` above, so it can
 * assume a plain `[-]digits[.digits]` shape — no scientific notation, which decimal wire values
 * (Rust's `rust_decimal::Decimal` serialized to a string) never produce.
 */
function roundHalfUpToTwoDecimals(raw: string): string {
  const trimmed = raw.trim();
  const negative = trimmed.startsWith('-');
  const unsigned = trimmed.replace(/^[+-]/, '');
  const [intPartRaw, fracPartRaw = ''] = unsigned.split('.');
  const intPart = intPartRaw === '' ? '0' : intPartRaw;

  if (fracPartRaw.length <= 2) {
    return `${negative ? '-' : ''}${intPart}.${fracPartRaw.padEnd(2, '0')}`;
  }

  const kept = fracPartRaw.slice(0, 2);
  const roundUp = fracPartRaw.charCodeAt(2) >= 53; // '5'.charCodeAt(0) === 53
  const cents = BigInt(intPart + kept) + (roundUp ? 1n : 0n);
  const centsStr = cents.toString().padStart(3, '0');
  const whole = centsStr.slice(0, -2);
  const cent = centsStr.slice(-2);
  return `${negative && cents !== 0n ? '-' : ''}${whole}.${cent}`;
}

/**
 * Litres display: exactly two decimals, never collapsed to a whole number (30 -> "30.00"). Bare
 * number without a unit — use {@link formatLitersUnit} when the value stands alone and needs "л".
 */
export function formatLiters(value: string | number | null | undefined): string {
  return formatDecimal2(value);
}

/** Volume with unit: "30.00 л" (or `—`). */
export function formatLitersUnit(value: string | number | null | undefined): string {
  const s = formatDecimal2(value);
  return s === EM_DASH ? s : `${s} л`;
}

/** Money: "30.00 ₽" (or `—`). Two decimals + руб sign. */
export function formatMoney(value: string | number | null | undefined): string {
  const s = formatDecimal2(value);
  return s === EM_DASH ? s : `${s} ₽`;
}

/** Per-litre price: "59.00 ₽/л" (or `—`). */
export function formatPricePerLiter(value: string | number | null | undefined): string {
  const s = formatDecimal2(value);
  return s === EM_DASH ? s : `${s} ₽/л`;
}

/**
 * Same two-decimal rounding as {@link formatDecimal2}, but for an EDITABLE numeric `<Input>`
 * value rather than read-only text: no unit suffix (the field must submit back as a plain
 * number), and empty/null/undefined resolves to `''` (a blank field to type into), not the em
 * dash — an em dash isn't something an admin can edit over. A genuinely non-numeric value is
 * passed through unchanged rather than blanked, so a bad wire value stays visible/fixable
 * instead of silently disappearing.
 */
export function toEditableDecimal2(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  if (Number.isNaN(Number(value))) return String(value);
  const raw = typeof value === 'number' ? value.toString() : value;
  return roundHalfUpToTwoDecimals(raw);
}

/** Local date+time via ru-RU locale (e.g. "28.06.2026, 13:00:00"). `—` for null / empty / invalid. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return EM_DASH;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  return d.toLocaleString('ru-RU');
}
